import React, { useState } from "react";
import { Typography } from "@material-ui/core";
import { Button } from "../fields/Button";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
    loginSetFlowStageWatcher,
    loginWatcher,
    mfaSetSmsNumber,
    setTranslatableSuccessMessage,
    verifySmsMfaCode
} from "../../redux/actions/loginactioncreators";
import phone from "phone";
import flowStages from "../../flowStages";
import { InputPhone } from "@sterling-react/input";
import OtpCodeEntry from "./MfaOtpCodeEntry";
import { MfaPageContainer, MfaPageBody, MfaPageTitle } from "./MfaShared";
import { SimpleErrorFormatter } from "./SimpleErrorFormatter";
import { mfaToastMessages } from "../../i18n/mfaToastMessages";

export default function MfaMobilePhone() {
    const dispatch = useDispatch();
    const userAttributes = useSelector((state) => state.loginForm.userInfo.UserAttributes);
    const userHasVerifiedPhoneNumber = (userAttributes.phone_number_verified && userAttributes.phone_number_verified === "true") ?? false;
    const mfaMobileNumber = useSelector((state) => state.loginForm.mfa.sms);
    const mfaSmsConfig = useSelector((state) => state.loginForm.mfa.config.sms);
    const otpCodeLength = mfaSmsConfig.codeLength;
    const [errorCode, setErrorCode] = useState();
    const [verificationCodeValid, setVerificationCodeValid] = useState();
    const [verificationCode, setVerificationCode] = useState();

    const doLogin = (payload) => { dispatch(loginWatcher(payload)) };
    const setMfaSmsNumber = (newValue) => { dispatch(mfaSetSmsNumber(newValue)) };
    const verify = (code) => { dispatch(verifySmsMfaCode({ code, step: "MFA_SETUP_SMS_VERIFY" })) };
    const setFlowStage = (payload) => { dispatch(loginSetFlowStageWatcher(payload)) };

    const challenge = useSelector((state) => state.loginForm.challenge);
    const username = useSelector((state) => state.loginForm.username);
    const password = useSelector((state) => state.loginForm.password);
    const accountNumber = useSelector((state) => state.loginForm.accountNumber);

    React.useEffect(() => {
        if (challenge.step === "MFA_SETUP_SMS_VERIFY") {
            dispatch(setTranslatableSuccessMessage(mfaToastMessages.smsVerificationCodeSent));
        }
    }, [challenge, mfaToastMessages, dispatch]);

    const handleError = (event, error, code) => {
        const errorMap = [
            <FormattedMessage key={0} id="MfaMobilePhoneValidationError0" defaultMessage="Invalid phone number — please review" />, // "IS_POSSIBLE": 0,
            <FormattedMessage key={1} id="MfaMobilePhoneValidationError1" defaultMessage="Invalid country code" />, // "INVALID_COUNTRY_CODE": 1,
            <FormattedMessage key={2} id="MfaMobilePhoneValidationError2" defaultMessage="The phone number is too short" />, // "TOO_SHORT": 2,
            <FormattedMessage key={3} id="MfaMobilePhoneValidationError3" defaultMessage="The phone number is too long" />, // "TOO_LONG": 3,
            <FormattedMessage key={4} id="MfaMobilePhoneValidationError4" defaultMessage="Invalid phone number — please review" />, // "IS_POSSIBLE_LOCAL_ONLY": 4,
            <FormattedMessage key={5} id="MfaMobilePhoneValidationError5" defaultMessage="Invalid phone number — please review" /> // "INVALID_LENGTH": 5,
        ];
        if (code >= 0) {
            setErrorCode([errorMap[code]]);
        } else {
            setErrorCode([]);
        }
    }
    const handleChange = (_, newValue) => {
        setMfaSmsNumber(newValue);
    }
    const handleClickBack = () => {
        setFlowStage({ flowStage: flowStages.MFA_SETUP })
    }
    const handleClickNext = () => {
        const payload = {
            flowStage: flowStages.CHALLENGE_MFA_SETUP,
            challengeResponse: {
                session: challenge.session,
                challengeName: challenge.challengeName,
                step: "MFA_SETUP_SMS",
                challengeParameters: {
                    USERNAME: challenge.challengeParameters.USER_ID_FOR_SRP,
                    MFA_CONFIG: {
                        PHONE_NUMBER: phone(mfaMobileNumber?.value)[0]
                    },
                    MFA_OPTIONS: challenge.challengeParameters["MFA_OPTIONS"] ?? undefined,
                    PASSWORD: password,
                    VERIFY: true
                }
            }
        };
        doLogin(payload);
    }
    const handleResendVerificationCode = () => {
        const payload = {
            flowStage: flowStages.CHALLENGE_MFA_SETUP,
            challengeResponse: {
                session: challenge.session,
                challengeName: challenge.challengeName,
                step: "MFA_SETUP_SMS",
                challengeParameters: {
                    USERNAME: username,
                    RESEND: "true",
                    MFA_CONFIG: {
                        PHONE_NUMBER: phone(mfaMobileNumber?.value)[0]
                    },
                    MFA_OPTIONS: challenge.challengeParameters["MFA_OPTIONS"] ?? undefined
                }
            },
            username: username,
            password: password,
            accountNumber: accountNumber
        };
        doLogin(payload);
    }
    const otpOnChange = (newValue) => {
        setVerificationCode(newValue);
        const valid = newValue.length === otpCodeLength;
        setVerificationCodeValid(valid);
    }
    const handleClickVerify = () => {
        verify(verificationCode);
    }

    const readyToVerify = challenge.step === "MFA_SETUP_SMS_VERIFY";
    const isError = (errorCode && errorCode.length > 0);
    const buttonDisabled = challenge.step === "MFA_SETUP_SMS_VERIFY" || isError || !mfaMobileNumber?.value;
    return (
        <div id="mobileNumber">
            <MfaPageContainer>
                <div className={readyToVerify && "hidden"}>
                    <MfaPageBody>
                        <MfaPageTitle>
                            <FormattedMessage tagName="div" id={"mfaSetup.smsSetupHeader"} defaultMessage="Phone Number" />
                        </MfaPageTitle>
                        <FormattedMessage id="mfaSetup.smsBlurb" defaultMessage="What phone number should we use for multi-factor authentication?" />
                        <InputPhone
                            id="mobileNumberEntry"
                            name="mobileNumberEntry"
                            onChange={handleChange}
                            testId="phone-input"
                            value={mfaMobileNumber?.value}
                            setValueCallback={handleChange}
                            setErrorCallback={handleError}
                            setErrorCallbackOnChangeDebounceTime={0}
                            allowDropdown={false}
                        />
                        {isError && <SimpleErrorFormatter id="mobilePhoneErrors" validationErrors={errorCode} />}
                        <div>
                            <Button
                                fullWidth={true}
                                id="backButton"
                                name="backButton"
                                onClick={handleClickBack}
                                variant="outlined"
                            >
                                <FormattedMessage id="mfaSetup.backButton" defaultMessage="Back" />
                            </Button>
                            <Button
                                fullWidth={true}
                                disabled={buttonDisabled}
                                id="mobileNumberNext"
                                name="mobileNumberNext"
                                onClick={handleClickNext}
                                variant="outlined"
                            >
                                <FormattedMessage id="mfaSetup.saveButton" defaultMessage="Next" />
                            </Button>
                        </div>
                    </MfaPageBody>

                </div>
                <div className={readyToVerify || "hidden"} >
                    <MfaPageTitle>
                        <FormattedMessage id="mfa.codeSent" defaultMessage="We just sent you a verification code" />
                    </MfaPageTitle>
                    <MfaPageBody>
                        <Typography>
                            <FormattedMessage
                                id="mfaSetup.smsSetupVerifyCode"
                                defaultMessage="Please allow up to 2 minutes to receive the code. In case you didn't receive the message, be sure to check your spam filter in your messages app."
                            />
                        </Typography>
                        <div>
                            <Button
                                id="submitResend"
                                name="submitResend"
                                onClick={userHasVerifiedPhoneNumber ? handleResendVerificationCode : handleClickNext}
                                variant="outlined"
                            >
                                <FormattedMessage id="mfa.resendOtpButton" description="A button to request another one-time password to be sent" defaultMessage="Resend code" />
                            </Button>
                        </div>
                        <OtpCodeEntry length={otpCodeLength} onChange={otpOnChange}/>
                        <div>
                            <Button
                                fullWidth={true}
                                id="backButton"
                                name="backButton"
                                onClick={handleClickBack}
                                variant="outlined"
                            >
                                <FormattedMessage id={"mfaSetup.backButton"} defaultMessage="Back" />
                            </Button>
                            <Button
                                fullWidth={true}
                                disabled={!verificationCodeValid}
                                id="verificationCodeVerify"
                                name="verificationCodeVerify"
                                onClick={handleClickVerify}
                                variant="outlined"
                            >
                                <FormattedMessage id={"mfaSetup.saveButton"} defaultMessage="Next" />
                            </Button>
                        </div>
                    </MfaPageBody>
                </div>
            </MfaPageContainer>
        </div >
    )
}